import React, { useState } from 'react';
import { Box, Text, render, useApp, useInput } from 'ink';
import { detectCliDirectoryContext, getContextLabel, getHomeActions, type HomeAction } from './context.js';

const BRAND_LINES = [
  '  ____  _                  _               _      _                    _       ',
  ' / ___|| |_ __ _ _ __   __| | __ _ _ __ __| |    / \\   __ _  ___ _ __ | |_ ___ ',
  ' \\___ \\| __/ _` | \'_ \\ / _` |/ _` | \'__/ _` |   / _ \\ / _` |/ _ \\ \'_ \\| __/ __|',
  '  ___) | || (_| | | | | (_| | (_| | | | (_| |  / ___ \\ (_| |  __/ | | | |_\\__ \\',
  ' |____/ \\__\\__,_|_| |_|\\__,_|\\__,_|_|  \\__,_| /_/   \\_\\__, |\\___|_| |_|\\__|___/',
  '                                                      |___/                     ',
] as const;

export interface HomeSelection {
  actionId: HomeAction['id'];
  argv: string[];
  preserveTuiScreen: boolean;
}

export function restoreTuiTerminal(stdout: NodeJS.WriteStream = process.stdout): void {
  void stdout;
}

interface HomeMenuProps {
  contextLabel: string;
  actions: HomeAction[];
  onSelect: (selection: HomeSelection | null) => void;
}

function HomeMenu({ contextLabel, actions, onSelect }: HomeMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { exit } = useApp();

  useInput((input, key) => {
    if (key.ctrl && input === 'c') {
      onSelect(null);
      exit();
      return;
    }
    if (input === 'q' || key.escape) {
      onSelect(null);
      exit();
      return;
    }
    if (key.upArrow || input === 'k') {
      setSelectedIndex((prev) => (prev - 1 + actions.length) % actions.length);
      return;
    }
    if (key.downArrow || input === 'j') {
      setSelectedIndex((prev) => (prev + 1) % actions.length);
      return;
    }
    if (key.return) {
      const chosen = actions[selectedIndex];
      if (!chosen.argv) {
        onSelect(null);
        exit();
        return;
      }
      onSelect({
        actionId: chosen.id,
        argv: chosen.argv,
        preserveTuiScreen: chosen.id === 'init',
      });
      exit();
    }
  });

  return (
    <Box flexDirection="column" paddingX={1}>
      <Box borderStyle="double" borderColor="cyan" paddingX={1} flexDirection="column">
        <Text color="cyanBright" bold>
          STANDARDAGENTS CLI
        </Text>
        <Text color="gray">Command Center</Text>
      </Box>
      <Box marginTop={1} flexDirection="column">
        {BRAND_LINES.map((line) => (
          <Text key={line} color="cyan">
            {line}
          </Text>
        ))}
      </Box>
      <Box marginTop={1} borderStyle="round" borderColor="gray" paddingX={1} flexDirection="column">
        <Text color="gray">
          Context: <Text color="whiteBright">{contextLabel}</Text>
        </Text>
        <Text color="gray">Use ↑/↓ (or j/k), Enter to select, q to exit.</Text>
      </Box>
      <Box marginTop={1} borderStyle="round" borderColor="cyan" paddingX={1} flexDirection="column">
        <Text color="cyanBright" bold>
          Actions
        </Text>
        {actions.map((action, index) => {
          const selected = selectedIndex === index;
          return (
            <Box key={action.id} flexDirection="column" marginBottom={0} marginTop={0}>
              <Text color={selected ? 'black' : 'white'} backgroundColor={selected ? 'cyan' : undefined} bold={selected}>
                {selected ? '›' : ' '} {action.label}
              </Text>
              <Text color={selected ? 'cyan' : 'gray'}>{`  ${action.description}`}</Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export async function runHomeTui(cwd: string = process.cwd()): Promise<HomeSelection | null> {
  if (!process.stdin.isTTY || !process.stdout.isTTY) return null;

  const context = detectCliDirectoryContext(cwd);
  const actions = getHomeActions(context);
  const contextLabel = getContextLabel(context);
  let selection: HomeSelection | null = null;
  const instance = render(
    <HomeMenu
      contextLabel={contextLabel}
      actions={actions}
      onSelect={(value) => {
        selection = value;
      }}
    />,
    { exitOnCtrlC: true },
  );
  await instance.waitUntilExit();
  instance.unmount();
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  return selection;
}
